/**
 * The EqualHeight module which handles making cards within wrappers have equal heights.
 * @class EqualHeight
 */
export default class EqualHeight {
  /**
   * EqualHeight constructor which assigns the element passed into the constructor
   * to the `this.element` property for later reference
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    /**
     * Reference to the DOM element that is the root of the component
     * @property {object} element - The DOM element that serves as the root of the component.
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} EqualHeight A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} EqualHeight A reference to the instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.wrappers = this.element.querySelectorAll('.stats__wrapper');

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.setEqualCardHeights = this.setEqualCardHeights.bind(this);
    this.onResize = this.onResize.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} EqualHeight A reference to the instance of the class
   * @chainable
   */
  enable() {
    // Run on load
    this.setEqualCardHeights();

    // Run on window resize for responsiveness
    window.addEventListener('resize', this.onResize);

    return this;
  }

  /**
   * Handle window resize events with debouncing
   * @returns {object} A reference to the current instance of the class
   */
  onResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.setEqualCardHeights();
    }, 100);

    return this;
  }

  /**
   * Sets equal heights for all cards within each wrapper
   * @returns {object} A reference to the current instance of the class
   */
  setEqualCardHeights() {
    this.wrappers.forEach((wrapper) => {
      const cards = wrapper.querySelectorAll('.stats__card');
      let maxHeight = 0;

      // Reset heights first
      cards.forEach((card) => {
        // eslint-disable-next-line no-param-reassign
        card.style.height = 'auto';
      });

      // Find the tallest card
      cards.forEach((card) => {
        const cardHeight = card.offsetHeight;
        if (cardHeight > maxHeight) maxHeight = cardHeight;
      });

      // Apply the tallest height to all cards
      cards.forEach((card) => {
        // eslint-disable-next-line no-param-reassign
        card.style.height = `${maxHeight}px`;
      });
    });

    return this;
  }
}
